import { ChangeEvent, useEffect, useState } from "react";
import { PlusCircleIcon } from "@heroicons/react/24/outline";
import DeleteCardModal from "@components/Modals/WarningModal";
import LoadingIcon from "@common/LoadingIcon";

type BusinessCardTableProps = {
  userId?: number;
  cardData?: any[];
  files: File[];
  loading: boolean;
  setFiles: (files: File[]) => void;
  setData: (data: File[]) => void;
  setIndexToDelete: (index: number[]) => void;
  disabled?: boolean;
};

const BusinessCardTable = ({
  userId,
  cardData,
  loading,
  files,
  setFiles,
  setData,
  setIndexToDelete,
  disabled = false,
}: BusinessCardTableProps) => {
  const warnModalTitle = "名刺を削除";
  const warnModalBody = "名刺を削除します。よろしいですか？";
  const warnModalConfirm = "削除する";
  const cardSides = ["表", "裏"];

  const [id, setId] = useState<number>(userId);
  const [businessCards, setBusinessCards] = useState<any>(
    cardData?.newFiles || files,
  );
  const [uploadStatus, setUploadStatus] = useState<string[]>([]);
  const [selectedCardIndex, setSelectedCardIndex] = useState<null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [cardsUpdated, setCardsUpdated] = useState<boolean>(false);

  useEffect(() => {
    setId(userId);
    if (!cardsUpdated) {
      setBusinessCards(cardData);
    }
  }, [userId, cardData]);

  const handleFileAddition = (
    event: ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const newFiles = Array.from(event.target.files!);
    setFiles((prevFiles) => {
      const updatedFiles = [...prevFiles];
      updatedFiles[index] = newFiles[0];
      return updatedFiles;
    });

    const newFileURLs = newFiles.map((file) => URL.createObjectURL(file));

    const updatedCards = [...businessCards];
    updatedCards[index] = {
      url: newFileURLs[0],
      blobId: null,
      index: index,
      local: true,
    };
    setBusinessCards(updatedCards);

    setData({ newFiles, index });

    setCardsUpdated(true);
  };

  const handleDelete = (index) => {
    const isPreview = businessCards[index]?.url?.includes?.("blob");

    console.log("Is preview image:", isPreview);
    const updatedCards = [...businessCards];
    const updatedStatus = [...uploadStatus];

    updatedCards[index] = {};
    updatedStatus[index] = "";

    setBusinessCards(updatedCards);
    setUploadStatus(updatedStatus);
    setData(updatedCards);
    setFiles(updatedCards.filter((card) => card.local));

    if (!isPreview) {
      setIndexToDelete((prev) => [...prev, index]);
    }
  };

  const handleOpenModal = (index) => {
    setSelectedCardIndex(index);
    setDeleteModalOpen(true);
  };

  const handleCancelDeleteModal = () => {
    setDeleteModalOpen(false);
  };

  const handleConfirm = () => {
    setDeleteModalOpen(false);
    handleDelete(selectedCardIndex);
  };

  return (
    <div className="mt-10 flex items-center gap-10 overflow-x-auto">
      {loading ? (
        <LoadingIcon />
      ) : (
        <>
          {cardSides.map((side, index) => {
            let cardUrl: string = "";
            let card: any;

            card = businessCards?.[index] ?? {};

            if (Object.keys(card).length > 0) {
              cardUrl = !disabled ? card?.url : card;
            }

            if (Array.isArray(files) && files.length > index && files[index]) {
              const file: File = files[index];
              const previewUrl: string | null = URL.createObjectURL(file);

              card =
                Array.isArray(cardData) && cardData.length > index
                  ? cardData[index]
                  : null;

              cardUrl =
                previewUrl ||
                (typeof card === "string" ? card : card?.url || "");
            }

            return (
              <div key={side} className="flex flex-col items-center min-w-100">
                {cardUrl ? (
                  <>
                    <img
                      src={cardUrl}
                      alt={`card-${index}`}
                      className="w-100 h-auto max-w-100"
                    />
                    <div className="flex gap-2 mt-5">
                      <div className="py-1 font-bold text-xl text-center justify-center">
                        名刺画像（{side}）
                      </div>
                      {!disabled && (
                        <>
                          <label
                            htmlFor={`fileInput-${index}`}
                            className="flex flex-col items-center text-black hover:opacity-50 cursor-pointer"
                          >
                            <input
                              id={`fileInput-${index}`}
                              type="file"
                              onChange={(e) => handleFileAddition(e, index)}
                              accept=".png,.jpg,.jpeg"
                              className="hidden"
                            />
                            <div className="text-sm rounded button-confirm hover:cursor-pointer">
                              選択
                            </div>
                          </label>
                          <div
                            className="text-sm rounded button-small-red hover:cursor-pointer"
                            onClick={() => handleOpenModal(index)}
                          >
                            削除
                          </div>
                        </>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <label
                      htmlFor={`fileInput-${index}`}
                      className="flex flex-col items-center p-5"
                    >
                      <input
                        id={`fileInput-${index}`}
                        type="file"
                        onChange={(e) => handleFileAddition(e, index)}
                        accept=".png,.jpg,.jpeg"
                        disabled={disabled}
                        className="hidden"
                      />
                      {!disabled ? (
                        <div className="flex w-80 h-60 items-center justify-center bg-gray-50 rounded-xl mb-3">
                          <PlusCircleIcon className="flex items-center justify-center stroke-mayo-link-text w-12 h-12 hover:opacity-50 cursor-pointer" />
                        </div>
                      ) : (
                        <div className="flex w-80 h-60 text-center justify-center bg-gray-50 rounded-xl mb-3">
                          <p className="flex font-bold items-center justify-center">
                            名刺なし
                          </p>
                        </div>
                      )}
                      <div className="font-bold text-xl mb-2 mt-5">
                        名刺画像（{side}）
                      </div>
                    </label>
                  </>
                )}
              </div>
            );
          })}

          <DeleteCardModal
            isOpen={deleteModalOpen}
            onRequestClose={handleCancelDeleteModal}
            onConfirm={handleConfirm}
            title={warnModalTitle}
            body={warnModalBody}
            confirmText={warnModalConfirm}
            cancelText={""}
          />
        </>
      )}
    </div>
  );
};

export default BusinessCardTable;
